import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import {
  faqs,
  howItWorks,
  skySuiteNames,
  SkySuiteContent,
  bunkerGANames,
  BunkerGAContent,
  cornerKickZoneNames,
  pitchsideLoungeNames,
  premiumBunkerNames,
  CornerKickZoneContent,
  PitchsideLoungeContent,
  PremiumBunkerContent,
  twentyPersonSuiteNames,
  twentyFivePersonSuiteNames,
  TwentyPersonSuiteContent,
  TwentyFivePersonSuiteContent,
  inventoryCardSuiteModal,
  SanAntonioFCSuitesInventoryCard,
  SanAntonioFCSportsAdditionalDetailsDisclaimerTC,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";

import Banner from "public/images/sanantoniofc/banner.png";
import LogoImage from "public/images/sanantoniofc/logo.png";
import BackgroundImage from "public/images/sanantoniofc/background.webp";

import suiteImage1 from "public/images/sanantoniofc/safc1.jpg";
import suiteImage2 from "public/images/sanantoniofc/safc2.jpg";
import suiteImage3 from "public/images/sanantoniofc/safc3.jpg";

const StadiumMapImage =
  "https://ss-leagues.s3.amazonaws.com/USL/Spurs/Stadium.Map.png";
const CornerKickImage =
  "https://ss-leagues.s3.amazonaws.com/USL/Spurs/Corner.Kick.View.jpeg";
const PitchsideLoungeImage =
  "https://ss-leagues.s3.amazonaws.com/USL/Spurs/Pitchside.Lounge.View.jpeg";
const PremiumBunkerImage =
  "https://ss-leagues.s3.amazonaws.com/USL/Spurs/Premium.Bunker.View.jpeg";
const BunkerGAImage =
  "https://ss-leagues.s3.amazonaws.com/USL/Spurs/GA.Bunker.View.jpeg";

const StandardSuiteImages = [suiteImage1.src, suiteImage2.src, suiteImage3.src];

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "sanantoniofc_suites",
  browserTitle: "San Antonio FC Sports Suites",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "San Antonio FC Sports",

  // seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",
    hide3dViewFromSlider: true,
    staticSeatMapImage: StadiumMapImage,
    customSeatMapStaticImageStyle: {
      width: "48%",
      marginX: "auto",
      display: "block",
    },

    suiteImages: {
      STE2: StandardSuiteImages,
      STE3: StandardSuiteImages,
      STE6: StandardSuiteImages,
      STE8: StandardSuiteImages,
      STE9: StandardSuiteImages,
      STE15: StandardSuiteImages,
      SKST: StandardSuiteImages,
      "130": [CornerKickImage],
      "127": [PitchsideLoungeImage],
      MBUNK1: [BunkerGAImage],
      MBUNK2: [PremiumBunkerImage],
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    hideViewTicketsInThankYouPage: true,

    isCheckoutAdditionalDetailsEnabled: true,
    isPerksEnabled: true,
    invPreviewAccountGroup: ["0"],
    allowCheckoutFromPreview: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    googleTagManagerId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/spurs/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",
  shouldUseInternalLogin: false,

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    seatMap3dPreviewImage: "",
    faviconImage: LogoImage.src,
    giftCardStandalonePageImage: "",
    findGamesBannerImage: Banner.src,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    logoImage: LogoImage.src + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: false,
    },

    hidePackageBalanceOnCheckout: true,
    hideServiceFee: true,

    inventoryCardSettings: {
      isPBLineItemsVisible: true,
      seatCountLabel: "Tickets Included",
      inventorySuitePackageInfo: [
        { suiteId: "Suite 2", FBPackageValue: "$400 Credit" },
        { suiteId: "Suite 3", FBPackageValue: "$400 Credit" },
        { suiteId: "Suite 6", FBPackageValue: "$500 Credit" },
        { suiteId: "Suite 8", FBPackageValue: "$400 Credit" },
        { suiteId: "Suite 9", FBPackageValue: "$400 Credit" },
        { suiteId: "Suite 15", FBPackageValue: "$400 Credit" },
        { suiteId: "Sky Suite", FBPackageValue: "$1,350 Credit" },
        { suiteId: "CornerKick Zone", FBPackageValue: "$15 Credit per seat" },
        { suiteId: "Pitchside Lounge", FBPackageValue: "$15 Credit per seat" },
        { suiteId: "Premium Bunker", FBPackageValue: "All You Can Eat package" },
        { suiteId: "Bunker GA", FBPackageValue: "All You Can Eat package" },
      ],
      pbLineItemsValue: 5,

      suiteLevelInventoryCard: {
        customButtons: [
          {
            id: 1,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <TwentyPersonSuiteContent />,
              });
            },
            suiteNames: twentyPersonSuiteNames,
          },
          {
            id: 2,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <TwentyFivePersonSuiteContent />,
              });
            },
            suiteNames: twentyFivePersonSuiteNames,
          },
          {
            id: 3,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <SkySuiteContent />,
              });
            },
            suiteNames: skySuiteNames,
          },
          {
            id: 4,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <CornerKickZoneContent />,
              });
            },
            suiteNames: cornerKickZoneNames,
          },
          {
            id: 5,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <PitchsideLoungeContent />,
              });
            },
            suiteNames: pitchsideLoungeNames,
          },
          {
            id: 6,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <PremiumBunkerContent />,
              });
            },
            suiteNames: premiumBunkerNames,
          },
          {
            id: 7,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <BunkerGAContent />,
              });
            },
            suiteNames: bunkerGANames,
          },
        ],
        renderBody: [
          {
            id: 1,
            component: () => <SanAntonioFCSuitesInventoryCard />,
          },
        ],
      },
    },
    inventoryContainer: {
      getGridCellHeight: (arg) => (arg?.mdDown ? 260 : 180),
    },

    hamburgerMenu: {
      logoutButtonEnabled: false,
    },

    gameFiltersSettings: {
      hideSectionFilter: true,
      hideAccessibilityFilter: true,
    },
  },

  colors: {
    primary: "#000000",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    additionalDetailsDisclaimerTC: (
      <SanAntonioFCSportsAdditionalDetailsDisclaimerTC />
    ),
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: null,
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutServiceNote:
      "Your reservation is not yet complete until notification from the San Antonio FC Sports confirming your reservation has been accepted in accordance with our agreement.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
